(() => {
  if (customElements.get('cc-reviews')) return;
  const el = (tag, cls, text) => {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  };
  const safeURL = value => {
    try { const url = new URL(value); return url.protocol === 'https:' ? url.href : ''; } catch { return ''; }
  };
  const stars = value => {
    const score = Math.max(0, Math.min(5, Number(value) || 0));
    const node = el('span', 'cc-review-stars');
    node.setAttribute('role', 'img');
    node.setAttribute('aria-label', `${score} out of 5 stars`);
    const base = el('span', 'cc-review-stars-base', '★★★★★');
    const fill = el('span', 'cc-review-stars-fill', '★★★★★');
    base.setAttribute('aria-hidden', 'true'); fill.setAttribute('aria-hidden', 'true');
    fill.style.width = `${score / 5 * 100}%`;
    node.append(base, fill); return node;
  };
  class CCReviews extends HTMLElement {
    connectedCallback() {
      if (this.started) { this.photoResize?.observe(this.photoTrack); return; }
      this.started = true; this.page = 0; this.sort = 'photos'; this.seen = new Set();
      this.photos = []; this.photoKeys = new Set();
      // Keep the page size stable if the viewport changes between requests.
      this.pageSize = window.matchMedia('(min-width: 768px)').matches ? 6 : 4;
      this.view = this.querySelector('.cc-reviews-custom');
      this.fallback = this.querySelector('.cc-reviews-fallback');
      this.grid = this.querySelector('.cc-reviews-grid');
      this.more = this.querySelector('.cc-reviews-more');
      this.status = this.querySelector('.cc-reviews-status');
      this.select = this.querySelector('.cc-reviews-sort');
      this.select.value = this.sort;
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('.cc-reviews-dialog-content');
      this.photoSection = this.querySelector('.cc-reviews-photos');
      this.photoTrack = this.querySelector('.cc-reviews-photo-track');
      this.photoCount = this.querySelector('.cc-reviews-photo-count');
      this.photoPrevious = this.querySelector('[data-review-photo-step="-1"]');
      this.photoNext = this.querySelector('[data-review-photo-step="1"]');
      this.photoMore = this.querySelector('.cc-reviews-gallery-more');
      this.galleryStatus = this.querySelector('.cc-reviews-gallery-status');
      this.photoNav = this.querySelector('.cc-reviews-photos-nav');
      this.base = `https://storefront-api.loox.io/storefront/v1/store/${encodeURIComponent(this.dataset.storeId)}/product-reviews`;
      this.more.addEventListener('click', () => this.load());
      this.select.addEventListener('change', () => { this.sort = this.select.value; this.load(true); });
      this.querySelector('.cc-reviews-close').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const box = this.dialog.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => { this.swipe = null; this.content.replaceChildren(); this.unlockPage(); this.opener?.focus({ preventScroll: true }); });
      this.photoPrevious.addEventListener('click', () => this.changePhoto(-1));
      this.photoNext.addEventListener('click', () => this.changePhoto(1));
      this.photoMore.addEventListener('click', () => this.loadMorePhotos());
      this.dialog.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault(); this.changePhoto(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.querySelector('.cc-reviews-photo-back').addEventListener('click', () => this.scrollPhotos(-1));
      this.querySelector('.cc-reviews-photo-forward').addEventListener('click', () => this.scrollPhotos(1));
      this.photoTrack.addEventListener('scroll', () => this.syncPhotoStrip(), { passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        this.photoResize = new ResizeObserver(() => this.syncPhotoStrip());
        this.photoResize.observe(this.photoTrack);
      }
      this.content.addEventListener('touchstart', event => {
        this.swipe = null;
        if (event.touches.length !== 1 || !event.target.closest?.('.cc-reviews-gallery-image')) return;
        const point = event.touches[0];
        this.swipe = { id: point.identifier, x: point.clientX, y: point.clientY };
      }, { passive: true });
      this.content.addEventListener('touchmove', event => {
        if (!this.swipe) return;
        if (event.touches.length !== 1) { this.swipe = null; return; }
        const point = event.touches[0];
        const dx = point.clientX - this.swipe.x, dy = point.clientY - this.swipe.y;
        if (Math.abs(dy) > 12 && Math.abs(dy) > Math.abs(dx)) { this.swipe = null; return; }
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) && event.cancelable) event.preventDefault();
      }, { passive: false });
      this.content.addEventListener('touchend', event => {
        const start = this.swipe; this.swipe = null;
        if (!start) return;
        const point = [...event.changedTouches].find(touch => touch.identifier === start.id);
        if (!point) return;
        const dx = point.clientX - start.x, dy = point.clientY - start.y;
        if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.3) this.changePhoto(dx < 0 ? 1 : -1);
      }, { passive: true });
      this.content.addEventListener('touchcancel', () => { this.swipe = null; });
      // Retain the original app widget until real API data has loaded successfully.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.init(); }
        }, { rootMargin: '600px' });
        this.observer.observe(this);
      } else this.init();
    }
    async get(url) {
      const response = await fetch(url, { credentials: 'omit', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error(`Review request failed: ${response.status}`);
      return response.json();
    }
    async init() {
      try {
        const summary = await this.get(`${this.base}/bottomline/${encodeURIComponent(this.dataset.productId)}`);
        if (!Number.isFinite(summary.totalReviews) || !Number.isFinite(summary.averageRating)) throw new Error('Invalid review summary');
        this.querySelector('.cc-reviews-summary').replaceChildren(stars(summary.averageRating), el('span', '', `${summary.averageRating.toFixed(1)} · ${summary.totalReviews} ${summary.totalReviews === 1 ? 'Review' : 'Reviews'}`));
        await this.load(true, true);
        this.loadAllPhotos();
      } catch { /* The original, functional Loox widget remains visible. */ }
    }
    // The strip shows every photo review, not only those on the loaded pages.
    // Featured ordering lists media reviews before text-only ones, so one or two
    // large pages cover them all; the first text-only review ends the walk.
    async loadAllPhotos() {
      if (this.allPhotos) { this.allPhotos.forEach(review => this.addPhotos(review)); this.finishPhotos(); return; }
      if (this.photosLoading) return;
      this.photosLoading = true;
      try {
        const reviews = [];
        for (let page = 1; page <= 10; page++) {
          const query = new URLSearchParams({ product_id: this.dataset.productId, limit: '100', page: String(page), sort: 'featured', direction: 'desc' });
          const data = await this.get(`${this.base}?${query}`);
          if (!Array.isArray(data.reviews) || !data.pagination) throw new Error('Invalid reviews');
          let text = false;
          for (const review of data.reviews) {
            if (String(review.product?.id) !== this.dataset.productId) continue;
            if ((review.media || []).some(item => item.type === 'photo')) reviews.push(review); else text = true;
          }
          if (text || !data.pagination.hasMore) break;
        }
        // Keep five-star photos first, as the photo-first review list does.
        reviews.sort((a, b) => (Number(b.rating) || 0) - (Number(a.rating) || 0));
        this.allPhotos = reviews;
        reviews.forEach(review => this.addPhotos(review));
        this.finishPhotos();
      } catch { /* The strip keeps the photos from the loaded review pages. */ }
      finally { this.photosLoading = false; }
    }
    finishPhotos() {
      this.photosComplete = true; this.photoMore.hidden = true;
      this.photoSection.hidden = !this.photos.length;
      this.syncPhotoStrip();
    }
    async load(reset = false, initial = false) {
      if (this.loading) return;
      this.loading = true; this.more.disabled = true; this.select.disabled = true;
      this.status.textContent = 'Loading reviews…';
      let next = reset ? 1 : this.page + 1;
      let rating = reset ? 5 : this.rating ?? 5;
      if (this.sort === 'photos' && !reset && this.ratingFinished) { rating--; next = 1; }
      const sort = ['highest', 'lowest'].includes(this.sort) ? 'rating' : this.sort;
      try {
        let data;
        do {
          const query = new URLSearchParams({ product_id: this.dataset.productId, limit: String(this.pageSize), page: String(next), sort: this.sort === 'photos' ? 'featured' : sort, direction: this.sort === 'lowest' ? 'asc' : 'desc' });
          // Featured puts media before text; rating groups keep five-star reviews first.
          // Exhaust every rating group, so all published reviews remain accessible.
          if (this.sort === 'photos') query.set('rating', String(rating));
          data = await this.get(`${this.base}?${query}`);
          if (!Array.isArray(data.reviews) || !data.pagination) throw new Error('Invalid reviews');
          if (this.sort !== 'photos' || data.reviews.length || rating <= 1) break;
          rating--; next = 1;
        } while (true);
        const fragment = document.createDocumentFragment();
        const seen = reset ? new Set() : new Set(this.seen);
        if (reset) { this.photos = []; this.photoKeys.clear(); this.photoTrack.replaceChildren(); }
        for (const review of data.reviews) {
          if (String(review.product?.id) !== this.dataset.productId || seen.has(review.id)) continue;
          this.addPhotos(review);
          fragment.append(this.card(review)); seen.add(review.id);
        }
        if (reset) this.grid.replaceChildren();
        this.grid.append(fragment); this.seen = seen; this.page = next; this.appliedSort = this.sort;
        this.rating = rating; this.ratingFinished = !data.pagination.hasMore;
        this.more.hidden = !data.pagination.hasMore && (this.sort !== 'photos' || rating <= 1);
        if (reset && this.allPhotos) this.allPhotos.forEach(review => this.addPhotos(review));
        this.photoSection.hidden = !this.photos.length;
        this.photoMore.hidden = this.more.hidden || Boolean(this.photosComplete);
        this.syncPhotoStrip();
        this.status.textContent = this.seen.size ? '' : 'No reviews yet.';
        this.view.hidden = false; this.fallback.hidden = true;
      } catch {
        this.status.textContent = 'Reviews could not be loaded. Please try again.';
        if (reset && !initial) { this.sort = this.appliedSort || 'photos'; this.select.value = this.sort; }
        if (initial) throw new Error('Initial reviews unavailable');
      } finally { this.loading = false; this.more.disabled = false; this.select.disabled = false; }
    }
    addPhotos(review) {
      for (const media of review.media || []) {
        const url = safeURL(media.url), key = `${review.id}|${url}`;
        if (media.type !== 'photo' || !url || this.photoKeys.has(key)) continue;
        this.photoKeys.add(key); this.photos.push({ key, media, review });
        this.photoTrack.append(this.photo(media, review, true));
      }
    }
    scrollPhotos(direction) {
      const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.photoTrack.scrollBy({ left: direction * this.photoTrack.clientWidth * .8, behavior: motion ? 'instant' : 'smooth' });
    }
    syncPhotoStrip() {
      const track = this.photoTrack;
      this.photoNav.hidden = track.scrollWidth <= track.clientWidth + 2;
      this.querySelector('.cc-reviews-photo-back').disabled = track.scrollLeft <= 1;
      this.querySelector('.cc-reviews-photo-forward').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }
    openPhoto(key, opener) {
      const index = this.photos.findIndex(photo => photo.key === key);
      if (index < 0) return;
      this.opener = opener; this.photoIndex = index;
      this.renderPhoto(); this.lockPage();
      try { this.dialog.showModal(); } catch (error) { this.unlockPage(); throw error; }
    }
    lockPage() {
      if (this.pageLock) return;
      const x = window.scrollX, y = window.scrollY;
      const changes = [];
      const set = (node, property, value) => {
        changes.push({ node, property, value: node.style.getPropertyValue(property), priority: node.style.getPropertyPriority(property) });
        node.style.setProperty(property, value);
      };
      this.pageLock = { x, y, changes };
      // Native modal dialogs do not stop background scrolling on mobile Safari.
      set(document.documentElement, 'overflow', 'hidden');
      set(document.body, 'position', 'fixed');
      set(document.body, 'top', `${-y}px`);
      set(document.body, 'left', `${-x}px`);
      set(document.body, 'width', '100%');
      set(document.body, 'overflow', 'hidden');
    }
    unlockPage() {
      if (!this.pageLock) return;
      const { x, y, changes } = this.pageLock;
      this.pageLock = null;
      for (const { node, property, value, priority } of changes) {
        if (value) node.style.setProperty(property, value, priority);
        else node.style.removeProperty(property);
      }
      window.scrollTo({ left: x, top: y, behavior: 'instant' });
    }
    async changePhoto(step) {
      if (!this.dialog.open || this.photoLoading || this.photos.length < 2) return;
      if (step > 0 && this.photoIndex === this.photos.length - 1 && !this.more.hidden && !this.photosComplete) {
        if (await this.loadMorePhotos()) return;
        if (!this.dialog.open) return;
      }
      this.photoIndex = (this.photoIndex + step + this.photos.length) % this.photos.length;
      this.renderPhoto();
    }
    async loadMorePhotos() {
      if (this.photoLoading || this.loading || this.more.hidden || this.photosComplete) return false;
      const before = this.photos.length;
      this.photoLoading = true; this.photoMore.disabled = true;
      this.galleryStatus.textContent = 'Loading photos…';
      // Skip text-only pages in small batches without fetching the entire catalog.
      for (let page = 0; page < 5 && this.photos.length === before && !this.more.hidden; page++) {
        await this.load();
        if (this.status.textContent === 'Reviews could not be loaded. Please try again.') break;
      }
      this.photoLoading = false; this.photoMore.disabled = false;
      if (this.photos.length > before) {
        this.photoIndex = before;
        if (this.dialog.open) this.renderPhoto();
        return true;
      }
      this.galleryStatus.textContent = this.more.hidden ? 'You have reached the last photo.' : 'No additional photos loaded. You can try loading more.';
      return false;
    }
    renderPhoto() {
      const { media, review } = this.photos[this.photoIndex];
      const name = review.reviewer?.name || 'Customer';
      const stage = el('div', 'cc-reviews-gallery-image');
      const img = el('img'); img.src = safeURL(media.url); img.alt = `Customer photo from ${name}`;
      img.addEventListener('error', () => { img.hidden = true; stage.append(el('p', '', 'This photo could not load. You can continue to the next photo.')); }, { once: true });
      stage.append(img);
      this.content.replaceChildren(stage, this.card(review, false, false));
      this.content.scrollTop = 0;
      this.photoCount.textContent = `${this.photoIndex + 1} / ${this.photos.length}`;
      this.galleryStatus.textContent = '';
      this.dialog.setAttribute('aria-label', `Customer photo from ${name}`);
      this.photoPrevious.disabled = this.photoNext.disabled = this.photos.length < 2;
    }
    photo(media, review, thumbnail = false) {
      const name = review.reviewer?.name || 'Customer';
      const button = el('button', thumbnail ? 'cc-review-photo cc-review-photo-thumb' : 'cc-review-photo'); button.type = 'button';
      button.setAttribute('aria-label', `View ${name}'s review photo`);
      const img = el('img'); img.src = safeURL(media.url); img.alt = `Customer photo from ${name}`; img.loading = 'lazy'; img.width = 240; img.height = 240;
      img.addEventListener('error', () => { img.hidden = true; button.append(el('span', '', 'View photo')); }, { once: true });
      button.append(img);
      button.addEventListener('click', () => this.openPhoto(`${review.id}|${safeURL(media.url)}`, button)); return button;
    }
    card(review, includeMedia = true, expandable = true) {
      const card = el('article', 'cc-review');
      const name = review.reviewer?.name || 'Customer';
      const media = includeMedia ? (review.media || []).filter(item => safeURL(item.url) && ['photo', 'video'].includes(item.type)) : [];
      if (media.length) {
        const holder = el('div', 'cc-review-media');
        const renderMedia = item => {
          if (item.type === 'photo') return this.photo(item, review);
          const video = el('video', 'cc-review-video'); video.src = safeURL(item.url); video.controls = true; video.preload = 'none'; video.playsInline = true; video.setAttribute('aria-label', `Review video from ${name}`); return video;
        };
        holder.append(renderMedia(media[0]));
        if (media.length > 1) { const thumbs = el('div', 'cc-review-thumbnails'); media.slice(1).forEach(item => thumbs.append(renderMedia(item))); holder.append(thumbs); }
        card.append(holder);
      } else card.classList.add('cc-review--text');
      const text = el('div', 'cc-review-copy');
      const author = el('div', 'cc-review-name'); author.append(el('strong', '', name));
      if (review.verified) author.append(el('span', 'cc-review-verified', '✓ Verified'));
      text.append(author);
      const date = new Date(review.date);
      if (!Number.isNaN(date.getTime())) { const time = el('time', 'cc-review-date', date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })); time.dateTime = date.toISOString(); text.append(time); }
      text.append(stars(review.rating));
      const body = el('p', 'cc-review-body', review.body || ''); text.append(body);
      if (expandable && (review.body || '').length > 140) {
        body.classList.add('is-collapsed');
        const toggle = el('button', 'cc-review-expand', 'Read more'); toggle.type = 'button'; toggle.setAttribute('aria-expanded', 'false');
        toggle.addEventListener('click', () => { const collapsed = body.classList.toggle('is-collapsed'); toggle.textContent = collapsed ? 'Read more' : 'Show less'; toggle.setAttribute('aria-expanded', String(!collapsed)); }); text.append(toggle);
      }
      if (review.reply?.body) text.append(el('div', 'cc-review-reply', `Clicked Craft replied:\n${review.reply.body}`));
      card.append(text); return card;
    }
    disconnectedCallback() {
      this.photoResize?.disconnect(); this.observer?.disconnect();
      if (this.dialog?.open) this.dialog.close();
      this.unlockPage();
    }
  }
  customElements.define('cc-reviews', CCReviews);
})();
